import React from "react";
import styled from "styled-components";

import { breakpoints, colors, fontFamily, sectionRhythm } from "../../../theme";
import { PROJECT_SHAKESPEERS } from "../../utils/constants/projects";
import ContactMeSection from "../contact-me-section";
import Testimonials from "../reviews-section/Testimonials";
import ContainerScroll from "../../components/container-scroll";
import { ProjectHero, caseStudyBodyStyles } from "./project-page-media";

const PageContainer = styled.div`
  width: calc(100vw - 48px);
  padding-top: ${sectionRhythm.paddingY.mobile}px;
  margin: 0 auto;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    width: 65%;
    max-width: 800px;
    padding-top: ${sectionRhythm.paddingY.desktop}px;
    margin: 0 auto;
  }
  @media only screen and (min-width: 900px) {
    width: 60%;
  }
`;

const Title = styled.h1`
  margin: 0 0 16px;
  font-family: ${fontFamily.extraBold};
  font-size: 24px;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    font-size: 44px;
    margin: 0 0 24px;
  }
`;

const OverviewSection = styled.div`
  margin-top: 2rem;
  margin-bottom: 0;
  display: grid;
  gap: 1.5rem;
`;

const OverviewRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
`;

const OverviewItem = styled.div`
  flex: 1;
  min-width: 140px;
`;

const OverviewLabel = styled.div`
  font-size: 14px;
  color: ${colors.grey.darkMedium};
  margin-bottom: 4px;
`;

const OverviewValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;

  a {
    color: inherit;
    word-break: break-all;

    &:hover {
      color: ${colors.linkHover.light};
      text-decoration: underline;
    }
  }
`;

const Body = styled.div`
  ${caseStudyBodyStyles}
`;

const ContentImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  object-fit: contain;
  border-radius: 18px;
  background: ${colors.white};
  margin: 1.5rem 0;
`;

const StackSubheading = styled.h5`
  && {
    margin-top: ${(props) => (props.$spaced ? "8px" : "0")};
  }
`;

const ShakespeersProjectSection = () => {
  const {
    client,
    services,
    industry,
    techStack,
    links,
    heading,
    media,
    roles,
    year,
  } = PROJECT_SHAKESPEERS;
  const heroSrc = media?.hero || media?.cover?.desktop;
  const yearLabel = year.length > 1 ? `${year[0]}-${year[1]}` : year[0];

  return (
    <>
      <PageContainer>
        <ContainerScroll
          titleComponent={<Title>{heading}</Title>}
          role={roles.join(", ")}
          year={yearLabel}
        >
          {heroSrc ? (
            <ProjectHero src={heroSrc} alt="Shakespeers landing hero" />
          ) : null}
        </ContainerScroll>

        <OverviewSection>
          <OverviewRow>
            <OverviewItem>
              <OverviewLabel>Client</OverviewLabel>
              <OverviewValue>{client.name}</OverviewValue>
            </OverviewItem>
            <OverviewItem>
              <OverviewLabel>Country</OverviewLabel>
              <OverviewValue>{client.country}</OverviewValue>
            </OverviewItem>
          </OverviewRow>
          <OverviewRow>
            <OverviewItem>
              <OverviewLabel>Team</OverviewLabel>
              <OverviewValue>Solo</OverviewValue>
            </OverviewItem>
            <OverviewItem>
              <OverviewLabel>Platform</OverviewLabel>
              <OverviewValue>Web (landing page and app)</OverviewValue>
            </OverviewItem>
          </OverviewRow>
          <OverviewRow>
            <OverviewItem>
              <OverviewLabel>Services provided</OverviewLabel>
              <OverviewValue>{services.join(", ")}</OverviewValue>
            </OverviewItem>
            <OverviewItem>
              <OverviewLabel>Industry</OverviewLabel>
              <OverviewValue>{industry}</OverviewValue>
            </OverviewItem>
          </OverviewRow>
          <OverviewItem>
            <OverviewLabel>Tech stack</OverviewLabel>
            <OverviewValue>{techStack.join(", ")}</OverviewValue>
          </OverviewItem>
          {links?.project ? (
            <OverviewItem>
              <OverviewLabel>Project link</OverviewLabel>
              <OverviewValue>
                <a
                  href={links.project}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {links.project}
                </a>
              </OverviewValue>
            </OverviewItem>
          ) : null}
        </OverviewSection>

        <Body>
          <section>
            <h4>The product</h4>
            <p>
              Before founding the US-based EdTech platform that enables children
              (ages 10-13) to write, read, and engage with content in a
              moderated online community, Elizabeth Slavitt built her career by
              working at McKinsey & Company before joining Khan Academy where
              she led teams responsible for developing educational content and
              learning tools. Her contributions to education earned her a place
              on the{" "}
              <a
                href="https://www.forbes.com/profile/elizabeth-slavitt/"
                no-referrer
                target="_blank"
              >
                Forbes 30 Under 30
              </a>{" "}
              list in Education in 2014. She reached out with the role of a Full
              stack developer to build the product quickly and efficiently. The
              first production release shipped in 3 months.
            </p>

            {media.community ? (
              <ContentImage
                src={media.community}
                alt="Story reading feed"
                loading="lazy"
              />
            ) : null}
          </section>

          <section>
            <h4>What was the challenge?</h4>
            <ul>
              <li>
                <strong>Safe space for children:</strong> The parents needed a
                safe space for their children to learn reading and writing
                without being exposed to inappropriate content .Reporting,
                moderation, and content writing rules were needed for that to
                happen.
              </li>
              <li>
                <strong>Four personas for the application:</strong> A persona is
                a type of user with their own goals and access in the product.
                Here that meant first-time visitors, parents, kids, and
                moderators. Their exact roles will be explained further down.
              </li>
              <li>
                <strong>A better writing tool for kids:</strong> The content
                needed structure, not a plain text box. The editor had to
                support formatting so kids could shape longer pieces, while
                being simple enough for their age group.
              </li>
            </ul>
          </section>

          <section>
            <h4>What was shipped?</h4>
            <p>
              A first-time visitor landed on the landing page, could read the
              FAQs, check pricings, and then register as a parent through Stripe
              checkout. Once signed up, the parent could create child accounts
              and send each child an invitation email. The child used that
              invite to complete onboarding and get their own space for writing
              and reading content. Parents stayed in control of subscriptions
              and child accounts, and could also view other children's content
              and browse writing topics.
            </p>
            {media.parents ? (
              <ContentImage
                src={media.parents}
                alt="Parent dashboard with child accounts"
                loading="lazy"
              />
            ) : null}
            <p>
              Inside the app, the children had a what-you-see-is-what-you-get
              editor to format content with a real structure instead of plain
              text. They received daily writing prompts, could draft and submit
              new pieces through a 7-day submission workflow, and were allowed
              to search content by topic, open them, and react with emojis.
              Image uploads were left out on purpose, to keep their space clean
              and to also reduce privacy risk. If something felt wrong, a child
              could report the content which was then reviewed by moderators.
            </p>
            {media.writing ? (
              <ContentImage
                src={media.writing}
                alt="Story editor"
                loading="lazy"
              />
            ) : null}
            {media.submission ? (
              <ContentImage
                src={media.submission}
                alt="Writing submission flow"
                loading="lazy"
              />
            ) : null}
            <p>
              Moderators were needed because a kids community cannot run on
              trust alone. They reviewed reported content, got automated alerts
              when something needed attention. Alert automations were setup on
              the basis blacklisted words and emojis so unsafe material could be
              caught before it spread. Only the moderators had access to the
              alert automations and the blacklist. Parents and children were
              only allowed to make reports/suggestions.
            </p>
            {media.moderation ? (
              <ContentImage
                src={media.moderation}
                alt="Moderator reports view"
                loading="lazy"
              />
            ) : null}
          </section>

          <section>
            <h4>Selecting the full stack framework</h4>

            <StackSubheading>RedwoodJS</StackSubheading>
            <p>
              The project needed a single place for both the frontend and
              backend so features could move quickly without bouncing between
              multiple codebases. Shared GraphQL types and a predictable project
              structure were equally important, especially for a solo MVP. We
              considered Next.js with a custom API, Remix, and a traditional
              React with Express setup. RedwoodJS already brought React,
              GraphQL, Prisma, and serverless APIs together in one framework,
              making it the quickest path to getting the product in front of
              users instead of spending weeks assembling the stack.
            </p>

            <StackSubheading $spaced>CKEditor</StackSubheading>
            <p>
              Kids needed a writing experience that felt closer to a word
              processor than a plain text box. Stories needed headings,
              formatting, images, and enough flexibility to encourage
              creativity, but building an editor from scratch would have taken
              far too much of the project. TipTap, Quill, TinyMCE, and Slate
              were all considered. We chose CKEditor because it delivered a
              polished writing experience out of the box, letting us spend our
              time improving the storytelling experience instead of rebuilding
              an editor.
            </p>

            <StackSubheading $spaced>Other technologies</StackSubheading>
            <ul>
              <li>
                Running and maintaining servers would have taken time away from
                building the product. <strong>AWS Lambda</strong> handled the
                infrastructure and scaling, keeping the backend lightweight
                while leaving room for the application to grow.
              </li>

              <li>
                Parents, kids, and moderators all needed different levels of
                access from day one. Rather than building authentication and
                permissions ourselves, <strong>Clerk</strong> already solved
                that problem and let development stay focused on the product.
              </li>

              <li>
                Stories, users, subscriptions, and moderation all depended on
                connected data. <strong>Prisma</strong> and{" "}
                <strong>PostgreSQL</strong> provided a clear data model without
                adding unnecessary complexity to the backend.
              </li>

              <li>
                Parents needed a checkout and subscription flow they already
                trusted. <strong>Stripe</strong> handled payments and recurring
                billing, keeping what could have become a large piece of work as
                a solved problem.
              </li>

              <li>
                As changes became more frequent, every release needed to stay
                reliable. <strong>Vercel</strong> and{" "}
                <strong>GitHub Actions</strong> automatically ran linting,
                tests, and deployments, helping catch problems before they
                reached production.
              </li>

              <li>
                Moderators needed to know when new reports arrived without
                constantly checking a dashboard. <strong>AWS SNS</strong> sent
                notifications whenever moderation needed attention, making the
                review process much easier to manage.
              </li>
            </ul>
          </section>

          <section>
            <h4>Testimonials</h4>
            <Testimonials showMore={false} reviews={client.reviews} />
          </section>
        </Body>
      </PageContainer>
      <ContactMeSection />
    </>
  );
};

export default ShakespeersProjectSection;
